import ExpanderPanel from '@rio-cloud/rio-uikit/ExpanderPanel';
import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import getSymbolFromCurrency from 'currency-symbol-map';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import { optionsActions } from '../../../application/redux/options/optionsReducer';
import { getCurrency, getVehicleCostsPerKilometer } from '../../../application/redux/options/optionsSelectors';
import type { FunctionProperties, NonFunctionProperties } from '../../../common/typescriptutils/typeManipulation';
import { gaPush, TRACKING_CATEGORIES } from '../../../configuration/setup/googleTagManager';
import type { AppDispatch, RootState } from '../../../configuration/setup/store';
import { LabeledNumberInput } from '../../common/displaycomponents/LabeledNumberInput';
import { LabeledSelect } from '../../common/displaycomponents/LabeledSelect';
import { supportedCurrencies } from './supportedCurrencies';

interface CostOptionProps {
    vehicleCostsPerKilometer: number;
    currency: string;
    updateVehicleCostsPerKilometer: (vehicleCostsPerKilometer: number) => void;
    updateCurrency: (currency: string) => void;
}

const currencyOptionLabels: SelectOption[] = supportedCurrencies.map(it => ({
    id: it,
    label: `${it} / ${getSymbolFromCurrency(it)}`,
}));

const CostOptions = (props: CostOptionProps) => {
    const { currency, vehicleCostsPerKilometer, updateVehicleCostsPerKilometer, updateCurrency } = props;
    const onChangeVehicleCostsPerKilometer = (value?: number) => {
        if (value != null) {
            updateVehicleCostsPerKilometer(value);
            gaPush({
                category: TRACKING_CATEGORIES.TOUR_OPTIONS_EDITOR,
                action: 'Specify vehicle costs',
                label: `${value}`,
            });
        }
    };
    const onChangeCurrency = (value?: string) => {
        if (value != null) {
            updateCurrency(value);
            gaPush({
                category: TRACKING_CATEGORIES.TOUR_OPTIONS_EDITOR,
                action: 'Select Currency',
                label: `${value}`,
            });
        }
    };
    return (
        <ExpanderPanel
            className={'VehicleOptions margin-bottom-0'}
            title={<FormattedMessage id={'intl-msg:routeplanning.label.costOptions'} />}
            iconLeft
            bsStyle={'separator'}
        >
            <div className={'display-flex align-items-end margin-bottom-10'}>
                <LabeledNumberInput
                    className={'flex-1-1-0 padding-right-5'}
                    labelId={'intl-msg:routeplanning.label.vehicleCostsPerKilometer'}
                    value={vehicleCostsPerKilometer}
                    integerOnly={false}
                    unit={getSymbolFromCurrency(currency)}
                    onChange={onChangeVehicleCostsPerKilometer}
                />
                <LabeledSelect
                    className={'padding-left-5'}
                    label={<FormattedMessage id={'intl-msg:routeplanning.label.currency'} />}
                    optionLabels={currencyOptionLabels}
                    selectedOption={currency}
                    onChange={onChangeCurrency}
                    dropup={true}
                    dropdownClassName={'left-auto right-0'}
                />
            </div>
        </ExpanderPanel>
    );
};

const mapStateToProps = (state: RootState): NonFunctionProperties<CostOptionProps> => ({
    vehicleCostsPerKilometer: getVehicleCostsPerKilometer(state),
    currency: getCurrency(state),
});
const mapDispatchToProps = (dispatch: AppDispatch): FunctionProperties<CostOptionProps> => ({
    updateVehicleCostsPerKilometer: (vehicleCostsPerKilometer: number) =>
        dispatch(optionsActions.setVehicleCostsPerKilometer(vehicleCostsPerKilometer)),
    updateCurrency: (currency: string) => dispatch(optionsActions.setCurrency(currency)),
});

export const CostOptionsContainer = connect(mapStateToProps, mapDispatchToProps)(CostOptions);
